import { logSkippedFact, type CventActivitiesBaseProperties } from './core';
import { mapReactionToCvent } from './reactionMapping';
import { resolveStreamOffsetSeconds, type StreamOffsetSources } from './streamOffset';
import { createGeneratedTrackMethods, type GeneratedTrackMethods } from './trackMethodsGenerated';

/** Minimal structural shape of a Vuex action context that can publish Cvent facts. */
export interface CventAnalyticsActionContext {
  rootGetters: {
    'event/getCventIdentity'?: CventActivitiesBaseProperties;
  };
}

/** Minimal shape of a persisted Q&A question post (Mattermost post). */
export interface CventQuestionPost {
  id?: string;
}

/** Extra fact properties known at the call site but not on the persisted post. */
export interface CventQuestionAskedMetadata {
  isAnonymous?: boolean;
}

/**
 * The fact's sessionId must be the Cvent session identifier — sourced from the
 * survey endpoint and exposed via `event/getCventIdentity` (WEBINAR-67983) —
 * NOT `route.params.id`, which is the Goldcast broadcast id. Using the route id
 * would attribute `session.question_asked` facts to the broadcast instead of
 * the Cvent session.
 */
function resolveSessionId(context: CventAnalyticsActionContext): string | undefined {
  return context.rootGetters['event/getCventIdentity']?.sessionId ?? undefined;
}

function logSkippedForMissingProperties(
  context: CventAnalyticsActionContext,
  activityType: string,
  properties: Record<string, unknown>
): void {
  logSkippedFact(
    context.rootGetters['event/getCventIdentity'],
    activityType,
    'missing required properties',
    properties
  );
}

function createStoreBackedTrackMethods(context: CventAnalyticsActionContext): GeneratedTrackMethods {
  return createGeneratedTrackMethods({
    collectBaseProperties: () => context.rootGetters['event/getCventIdentity'] as CventActivitiesBaseProperties,
    initialize: () => undefined
  });
}

/**
 * Publishes the `session.question_asked` fact for a persisted Q&A question.
 *
 * Both `sessionId` and `questionId` are contract-required (see
 * `actions/question_asked.json`), so the fact is skipped when either is
 * missing.
 *
 * Returns `true` when a fact was handed to the analytics core, `false` when it
 * was skipped (missing session/question id, incomplete Cvent identity, or
 * pipeline disabled).
 */
function trackQuestionAsked(
  context: CventAnalyticsActionContext,
  questionPost: CventQuestionPost | null | undefined,
  metadata: CventQuestionAskedMetadata = {}
): boolean {
  const sessionId = resolveSessionId(context);
  const questionId = questionPost?.id;
  if (!sessionId || !questionId) {
    logSkippedForMissingProperties(context, 'session.question_asked', { sessionId, questionId });
    return false;
  }

  return createStoreBackedTrackMethods(context).trackQuestionAsked({
    sessionId,
    questionId,
    ...metadata
  });
}

function trackReactionAdded(
  context: CventAnalyticsActionContext,
  reactionId: string,
  streamOffsetSources: StreamOffsetSources = {}
): boolean {
  const sessionId = resolveSessionId(context);
  if (!sessionId || !reactionId) {
    logSkippedForMissingProperties(context, 'session.reactionAdded', { sessionId, reactionId });
    return false;
  }

  const streamOffsetSeconds = resolveStreamOffsetSeconds(streamOffsetSources);

  return createStoreBackedTrackMethods(context).trackReactionAdded({
    sessionId,
    ...mapReactionToCvent(reactionId),
    ...(streamOffsetSeconds === undefined ? {} : { streamOffsetSeconds })
  });
}

/** Poll-vote fact properties known at the call site. */
export interface CventPollAnswerSubmittedPayload {
  pollId?: string;
  questionType?: 'singleChoice' | 'multipleChoice';
  submittedAnswer?: string;
}

/**
 * Publishes the `session.poll_answer_submitted` fact for a poll vote.
 *
 * Returns `true` when a fact was handed to the analytics core.
 */
function trackPollAnswerSubmitted(
  context: CventAnalyticsActionContext,
  payload: CventPollAnswerSubmittedPayload
): boolean {
  const sessionId = resolveSessionId(context);
  if (!sessionId || !payload.pollId || !payload.questionType || !payload.submittedAnswer) {
    logSkippedForMissingProperties(context, 'session.poll_answer_submitted', { sessionId, ...payload });
    return false;
  }

  return createStoreBackedTrackMethods(context).trackPollAnswerSubmitted({
    sessionId,
    pollId: payload.pollId,
    questionId: payload.pollId,
    questionType: payload.questionType,
    submittedAnswer: payload.submittedAnswer
  });
}

/**
 * Publishes the `session.chatMessageSent` fact for a session chat message the
 * server has accepted. `messageId` is the Mattermost post id; the message text
 * is never part of the fact.
 */
function trackChatMessageSent(
  context: CventAnalyticsActionContext,
  messageId: string | undefined,
  streamOffsetSources: StreamOffsetSources = {}
): boolean {
  const sessionId = resolveSessionId(context);
  if (!sessionId || !messageId) {
    logSkippedForMissingProperties(context, 'session.chatMessageSent', { sessionId, messageId });
    return false;
  }

  const streamOffsetSeconds = resolveStreamOffsetSeconds(streamOffsetSources);

  return createStoreBackedTrackMethods(context).trackChatMessageSent({
    sessionId,
    messageId,
    ...(streamOffsetSeconds === undefined ? {} : { streamOffsetSeconds })
  });
}

export const cventAnalytics = {
  trackQuestionAsked,
  trackReactionAdded,
  trackPollAnswerSubmitted,
  trackChatMessageSent
};
